/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Tag, Col, Select, Button, Tooltip } from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CheckOutlined,
  HourglassOutlined,
  FileSearchOutlined,
  SwapOutlined,
  PlayCircleOutlined,
  LoadingOutlined,
  FormOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Filters, FiltersCard } from "../../../components/Filters";
import { Label } from "../../../components/ui";

import api from "../../../services/api";

import { useToast } from "../../../hooks/toast";
// eslint-disable-next-line import/extensions
import Table from "../../../components/Table";
import UserAvatar from "../../../components/UserAvatar";
import { ICustomer } from "../../Customers";
import { IUser } from "../../Users";
import ProjectTypes from "../../../enums/ProjectTypes";
import { IRequirement } from "../../Project";
import ActivityStatus from "../../../enums/ActivityStatus";
import ActivityTypes from "../../../enums/ActivityTypes";
import ConcludeActivityModal from "../ConcludeActivityModal";
import AssignActivityModal from "../AssignActivityModal";
import ActivityActionsHistoryModal from "../ActivityActionsHistoryModal";
import EditActivityHoursModal from "../EditActivityHoursModal";
import HistoricActivityModal from "../../FactoryActivity/HistoricActivityModal";
import NameProjectTypes from "../../../enums/NameProjectTypes";
import StatusDateExpected from "../../FactoryActivity/StatusDateExpected";
import ProjectStatus from "../../../enums/ProjectStatus";

export interface IProject {
  id: string;
  identifier: string;
  short_description: string;
  full_description: string;
  contractor_name: string;
  contractor_email: string;
  contractor_phone_number: string;
  status: string;
  purchase_order: string;
  customer: ICustomer;
  manager: IUser;
  projectType: IProjectType;
}

export interface IProjectType {
  id: string;
  name: string;
  acronym: ProjectTypes;
}

const CommonActivities: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [loadingActivitiesExecuted, setLoadingActivitiesExecuted] = useState(
    false,
  );
  const [activities, setActivities] = useState<any[]>([]);
  const [activitiesExecuted, setActivitiesExecuted] = useState<any[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<IRequirement>();
  const [showFilters, setShowFilters] = useState(false);
  const [showExecutedFilters, setShowExecutedFilters] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState<boolean>(false);
  const [
    editActivityHoursModalVisible,
    setEditActivityHoursModalVisible,
  ] = useState<boolean>(false);
  const [
    activityActionsModalVisible,
    setActivityActionsModalVisible,
  ] = useState<boolean>(false);
  const [concludeModalVisible, setConcludeModalVisible] = useState<boolean>(
    false,
  );
  const [historicModalVisible, setHistoricModalVisible] = useState<boolean>(
    false,
  );
  const [startedActivity, setStartedActivity] = useState<string | null>(null);

  const { addToast } = useToast();
  const { t } = useTranslation(["activities"]);

  // Filters
  const [status, setStatus] = useState();
  const [type, setType] = useState();
  const [typeExeculted, setTypeExecuted] = useState();

  const searchParams = useMemo(() => {
    return {
      status,
      type,
      project_status: [
        ProjectStatus.WaitingDevelopment,
        ProjectStatus.Development,
      ],
    };
  }, [status, type]);

  const searchParamsConcluded = useMemo(() => {
    return {
      type: typeExeculted,
      project_status: [
        ProjectStatus.WaitingDevelopment,
        ProjectStatus.Development,
        ProjectStatus.Concluded,
      ],
    };
  }, [typeExeculted]);

  useEffect(() => {
    setLoading(true);
    api
      .get<IRequirement[]>("/activities", { params: searchParams })
      .then(response => {
        const activitiesNotExecuted = response?.data?.filter(
          activity => activity.status !== ActivityStatus.Executed,
        );

        setActivities(activitiesNotExecuted);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar as suas atividades, tente novamente.",
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    setLoadingActivitiesExecuted(true);
    api
      .get<IRequirement[]>("/activities", { params: searchParamsConcluded })
      .then(response => {
        const ativitiesConcluded = response?.data?.filter(
          activity => activity.status === ActivityStatus.Executed,
        );
        setActivitiesExecuted(
          ativitiesConcluded.sort((a, b) =>
            a.identifier.localeCompare(b.identifier),
          ),
        );
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar as suas atividades, tente novamente.",
        });
      })
      .finally(() => setLoadingActivitiesExecuted(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsConcluded]);

  const statusOptions = useMemo(() => {
    return (Object.keys(ActivityStatus) as (keyof typeof ActivityStatus)[])
      .map((key, _) => {
        return {
          label: t(`status.options.${ActivityStatus[key]}.label`),
          value: ActivityStatus[key],
        };
      })
      .filter(option => option.value !== ActivityStatus.Executed);
  }, [t]);

  const typeOptions = useMemo(() => {
    return (Object.keys(ActivityTypes) as (keyof typeof ActivityTypes)[]).map(
      (key, _) => {
        return {
          label: t(`types.options.${ActivityTypes[key]}.label`),
          value: ActivityTypes[key],
        };
      },
    );
  }, [t]);

  const handleOpenAssignModal = useCallback((activity: IRequirement) => {
    setSelectedActivity(activity);
    setAssignModalVisible(true);
  }, []);

  const handleOpenConcludeModal = useCallback((activity: IRequirement) => {
    setSelectedActivity(activity);
    setConcludeModalVisible(true);
  }, []);

  const handleOpenEditActivityHoursModal = useCallback(
    (activity: IRequirement) => {
      setSelectedActivity(activity);
      setEditActivityHoursModalVisible(true);
    },
    [],
  );

  const handleOpenHistoryActivityModal = useCallback(
    (activity: IRequirement) => {
      setSelectedActivity(activity);
      setHistoricModalVisible(true);
    },
    [],
  );

  const handleOpenActionsHistoryModal = useCallback(
    (activity: IRequirement) => {
      setSelectedActivity(activity);
      setActivityActionsModalVisible(true);
    },
    [],
  );

  const validActivityFactory = useCallback((activity: IRequirement) => {
    if (activity.status === ActivityStatus.Executed) {
      return false;
    }
    return activity?.project?.projectType?.acronym !== ProjectTypes.Allocation;
  }, []);

  const updateActivitiesConcluded = (activity: IRequirement): void => {
    setActivitiesExecuted(old =>
      [...old, activity].sort((a, b) =>
        a.identifier.localeCompare(b.identifier),
      ),
    );
    setActivities(old => old?.filter(act => act.id !== activity.id));
    setTypeExecuted(undefined);
    setShowExecutedFilters(false);
  };

  const handleCloseModal = useCallback(
    (activity?: IRequirement) => {
      setSelectedActivity(undefined);
      if (activity) {
        const index = activities.findIndex(act => act.id === activity.id);
        const newActivities = activities;

        if (index > -1) {
          newActivities[index] = activity;
        } else {
          newActivities.push(activity);
        }
        if (activity.status === ActivityStatus.Executed) {
          updateActivitiesConcluded(activity);
        } else {
          setActivities([...newActivities]);
        }
      }
      setHistoricModalVisible(false);
      setAssignModalVisible(false);
      setEditActivityHoursModalVisible(false);
      setConcludeModalVisible(false);
      setActivityActionsModalVisible(false);
    },
    [activities],
  );

  const beginActivity = useCallback(
    async (id: string) => {
      const date_begin_activity = moment(moment().utc()).format("YYYY-MM-DD");
      setStartedActivity(id);
      try {
        const activity = await api.patch(`/activities/${id}/update_activity`, {
          date_begin_activity,
        });

        setActivities((oldActivities: IRequirement[]) => {
          const index = oldActivities.findIndex(act => act.id === id);
          const newActivities = oldActivities;

          if (index > -1) {
            newActivities[index] = activity.data;
          } else {
            newActivities.push(activity.data);
          }
          return [...newActivities];
        });
      } catch (error) {
        console.log(error);
      }
      setStartedActivity(null);
    },
    [startedActivity],
  );

  const activitiesColumns: ColumnsType<IRequirement> = [
    {
      title: "Identificador do Projeto",
      dataIndex: "project",
      render: (_, record) => record.project?.identifier,
    },
    {
      title: t("project"),
      dataIndex: "project",
      render: (project: IProject) => {
        return (
          <>
            <Link to={`/app/projects/${project?.id}`}>
              {project?.short_description}
            </Link>
          </>
        );
      },
    },
    {
      title: t("identifier"),
      dataIndex: "identifier",
    },
    {
      title: t("description"),
      dataIndex: "description",
    },
    {
      title: t("types.label"),
      dataIndex: "type",
      render: (type: string) => {
        return (
          <Tag color={t(`types.options.${type}.color`)} key={type}>
            {t(`types.options.${type}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("table_column.status_dev"),
      dataIndex: "status_dev",
      render: (
        _,
        {
          expected_date_begin,
          date_begin_activity,
          expected_date_end,
          status,
        }: IRequirement,
      ) => {
        return (
          <StatusDateExpected
            beginDate={expected_date_begin}
            endDate={expected_date_end}
            date_begin_activity={date_begin_activity}
            status={status}
          />
        );
      },
    },
    {
      title: t("status.label"),
      dataIndex: "status",
      render: (status: string) => {
        return (
          <Tag color={t(`status.options.${status}.color`)} key={status}>
            {t(`status.options.${status}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("estimation"),
      dataIndex: "hours",
      render: (estimation: number) => estimation && `${estimation} h`,
    },
    {
      title: t("wasted_hours"),
      dataIndex: "wasted_hours",
      render: (wasted: number) => wasted && `${wasted} h`,
    },
    {
      title: t("consultant"),
      dataIndex: "consultant",
      render: (consultant: IUser) => {
        if (consultant) {
          return (
            <UserAvatar
              name={consultant.name}
              url={consultant.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
    },
    {
      title: t("table_column.expected_date_begin"),
      dataIndex: "expected_date_begin",
      render: (expected_date_begin: string) => {
        const beginDate = moment(expected_date_begin);
        if (!moment(beginDate).isValid()) return "";
        return <>{moment(expected_date_begin).format("DD/MM/YYYY")}</>;
      },
    },
    {
      title: t("table_column.expected_date_end"),
      dataIndex: "expected_date_end",
      render: (expected_date_end: string) => {
        const endDate = moment(expected_date_end);
        if (!moment(endDate).isValid()) return "";
        return <>{moment(expected_date_end).format("DD/MM/YYYY")}</>;
      },
    },
    {
      title: t("actions.label"),
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          {record.status !== ActivityStatus.Executed && (
            <>
              <Tooltip title="Atribuir">
                <Button
                  onClick={() => handleOpenAssignModal(record)}
                  type="link"
                  icon={<SwapOutlined />}
                />
              </Tooltip>
              <Tooltip title="Concluir">
                <Button
                  onClick={() => handleOpenConcludeModal(record)}
                  type="link"
                  icon={<CheckOutlined style={{ color: "green" }} />}
                />
              </Tooltip>
            </>
          )}
          {(record.status === ActivityStatus.Assigned ||
            record.status === ActivityStatus.Development) && (
            <Tooltip title="Apontar horas">
              <Button
                onClick={() => handleOpenEditActivityHoursModal(record)}
                type="link"
                icon={<HourglassOutlined style={{ color: "green" }} />}
              />
            </Tooltip>
          )}
          {validActivityFactory(record) && (
            <>
              <Tooltip
                title={
                  record?.date_begin_activity
                    ? "Inserir Nota de desenvolvimento"
                    : "Inserir Nota de Impedimento"
                }
              >
                <Button
                  onClick={() => handleOpenHistoryActivityModal(record)}
                  type="link"
                  icon={
                    <FormOutlined
                      style={{
                        color: record?.date_begin_activity ? "green" : "red",
                      }}
                    />
                  }
                />
              </Tooltip>
              <Tooltip title="Iniciar Atividade 1">
                <Button
                  onClick={() => beginActivity(record.id)}
                  type="link"
                  icon={
                    startedActivity === record.id ? (
                      <LoadingOutlined />
                    ) : (
                      <PlayCircleOutlined />
                    )
                  }
                  disabled={
                    /*               !record?.expected_date_end ||
                    !record?.expected_date_begin || */
                    !!record.date_begin_activity ||
                    startedActivity === record.id ||
                    record.status === ActivityStatus.Executed
                  }
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Ver histórico">
            <Button
              onClick={() => handleOpenActionsHistoryModal(record)}
              type="link"
              icon={<FileSearchOutlined />}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const handleShowFilters = useCallback(
    value => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showFilters) {
        setStatus(undefined);
        setType(undefined);
      }
    },
    [showFilters],
  );

  const handleShowExecutedFilters = useCallback(
    value => {
      setShowExecutedFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showExecutedFilters) {
        setTypeExecuted(undefined);
      }
    },
    [showExecutedFilters],
  );

  const activitiesFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("status.label")}</Label>
            </div>
            <Select
              placeholder={t("status.label")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              options={statusOptions}
              onChange={value => setStatus(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("types.label")}</Label>
            </div>
            <Select
              placeholder={t("types.label")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              options={typeOptions}
              onChange={value => setType(value)}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [showFilters, statusOptions, t, typeOptions],
  );
  const activitiesExecutedFilters = useMemo(
    () =>
      showExecutedFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("types.label")}</Label>
            </div>
            <Select
              placeholder={t("types.label")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              options={typeOptions}
              onChange={value => setTypeExecuted(value)}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [showExecutedFilters, t, typeOptions],
  );

  return (
    <div style={{ marginBottom: "16px" }}>
      <AssignActivityModal
        activity={selectedActivity}
        visible={assignModalVisible}
        handleClose={handleCloseModal}
      />
      <ConcludeActivityModal
        activity={selectedActivity}
        visible={concludeModalVisible}
        handleClose={handleCloseModal}
      />
      <HistoricActivityModal
        activity={selectedActivity}
        visible={historicModalVisible}
        handleClose={handleCloseModal}
      />
      <EditActivityHoursModal
        activity={selectedActivity}
        visible={editActivityHoursModalVisible}
        handleClose={handleCloseModal}
      />
      <ActivityActionsHistoryModal
        activity={selectedActivity}
        visible={activityActionsModalVisible}
        handleClose={handleCloseModal}
      />
      <Table
        data={activities}
        columns={activitiesColumns}
        title={
          <>
            Minhas atividades
            <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            />
          </>
        }
        footer={() => <></>}
        loading={loading}
        pagination={false}
        extraFilters={activitiesFilters}
      />
      <br />

      {children}

      <Table
        data={activitiesExecuted}
        columns={activitiesColumns}
        title={
          <>
            Atividades Finalizadas
            <Filters
              showFilters={showExecutedFilters}
              setShowFilters={handleShowExecutedFilters}
            />
          </>
        }
        footer={() => <></>}
        loading={loadingActivitiesExecuted}
        extraFilters={activitiesExecutedFilters}
        pagination
      />
    </div>
  );
};

export default CommonActivities;
